import { PureComponent, createElement as h, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { Spin } from 'antd'
import { isElement } from '../core'
import { k3, LOAD_CLASS, MASK_CLASS } from '../static/css'

interface IBpmLoadingState {
  /** 全屏下的加载框是否展示 */
  fullScreenOpen: boolean
  /** dom */
  maskDomSet: Set<HTMLElement>
}

function Loading() {
  return (
    createPortal(
      h('div',
        {
          className: `${MASK_CLASS} b-fx b-j-c`,
          style:     {
            position: 'fixed',
            top:      0,
            left:     0,
            right:    0,
            bottom:   0
          }
        }, h(Spin, { size: 'large' })),
      document.body
    )
  )
}

class BpmLoading extends PureComponent<unknown, IBpmLoadingState> {

  // 200ms内返回回来的数据，就不需要显示加载框
  static LAZY_SHOW_TIME = 200

  state = {
    /** 全屏下的加载框是否展示 */
    fullScreenOpen: false,
    /** dom */
    maskDomSet:     new Set<HTMLElement>()
  }

  /** 全屏下的加载框 缓存栈 */
  fullScreenStack = 0
  /** 全屏下的加载框 延时定时器 */
  fullScreenTimer = null
  /** 存储全局定时器 */
  timer!: NodeJS.Timer

  /**
   * 防止show的时候，html页面可以滚动
   * @param fix  true|页面不能滚动  false|页面可滚动
   */
  fixHtml = (fix: boolean) => {
    const { documentElement: html, body } = document
    if (fix) {
      body.style.cssText += `top:-${html.scrollTop}px;left:0px;right:0;${k3}`
    } else {
      body.style.position = 'static'
      html.scrollTop = -parseInt(body.style.top)
      body.style.top = ''
    }
  }

  show = (addMaskDom: HTMLElement) => {
    const nextMaskSet = this.memoryRecycle()
    if (isElement(addMaskDom) && document.contains(addMaskDom)) {
      return this.showMaskDom(addMaskDom, nextMaskSet)
    }
    if (this.fullScreenStack++ === 0) {
      this.timer = setTimeout(() => {
        if (this.fullScreenStack !== 0) {
          this.timer = null!
          this.fixHtml(true)
          this.setState({ fullScreenOpen: true })
        }
      }, BpmLoading.LAZY_SHOW_TIME)
    }
  }

  close = (closeMaskDom: HTMLElement) => {
    if (isElement(closeMaskDom) && closeMaskDom.parentElement) {
      return this.closeMaskDom(closeMaskDom)
    }
    // 可能会调用多次close，就会存在问题，return掉
    if (!this.fullScreenStack--) return
    if (this.fullScreenStack === 0) {
      if (this.timer) clearTimeout(this.timer)
      // 如果this.timer不存在，说明已经调用过了fixHtml(true)，需要清除
      else this.fixHtml(false)
      this.setState({ fullScreenOpen: false })
    }
  }

  /** 在某一个dom上显示加载蒙版 */
  showMaskDom = (addMaskDom: HTMLElement, maskDomSet: Set<HTMLElement>) => {
    // 已经存在，不需要处理了
    if (maskDomSet.has(addMaskDom) || addMaskDom['_timer']) return
    addMaskDom['_timer'] = setTimeout(() => {
      addMaskDom['_timer'] = null
      // 添加上样式
      if (getComputedStyle(addMaskDom).position === 'static') {
        addMaskDom.classList.add('b-rel')
      }
      this.setState(({ maskDomSet }) => {
        maskDomSet.add(addMaskDom)
        return {
          maskDomSet: new Set(maskDomSet)
        }
      })
    }, BpmLoading.LAZY_SHOW_TIME)
  }

  /** 在某一个dom上关闭显示加载蒙版 */
  closeMaskDom = (closeMaskDom: HTMLElement) => {
    const { maskDomSet } = this.state
    // 如果定时器存在，那么直接移除定时器就行
    if (closeMaskDom['_timer']) {
      clearTimeout(closeMaskDom['_timer'])
      closeMaskDom['_timer'] = null
    }
    // maskDomSet不存在，说明之前没有设置过，不需要处理了
    if (!maskDomSet.has(closeMaskDom)) return
    // 定时器不存在，说明加载面板已经存在
    closeMaskDom.classList.remove('b-rel')
    maskDomSet.delete(closeMaskDom)
    this.setState({
      maskDomSet: new Set(maskDomSet)
    })
  }

  /** 垃圾回收 */
  memoryRecycle = () => {
    // 判断现在正在加载的maskDomSet是否挂载在树上，如果不在
    // 说明这个dom已经被移除了 为了防止内存泄露，需要移除
    let exists = false
    const { maskDomSet } = this.state
    maskDomSet.forEach(dom => {
      if (!document.contains(dom)) {
        maskDomSet.delete(dom)
        exists = true
      }
    })
    if (exists) {
      return new Set(maskDomSet)
    } else {
      return maskDomSet
    }
  }

  render() {

    return (
      h(
        Fragment,
        null,
        [
          this.state.fullScreenOpen && h(Loading, { key: -999 }),
          Array.from(this.state.maskDomSet).map((item, index) => (
            createPortal(
              h(
                'div',
                { className: `b-fx b-j-c b-abs ${MASK_CLASS}`, key: index },
                h(Spin)
              ),
              item
            )
          ))
        ]
      )
    )
  }

}

const loadingDom = document.createElement('div')
loadingDom.className = LOAD_CLASS
document.body.appendChild(loadingDom)

export {
  BpmLoading,
  // Spin,
  loadingDom
}